/**
 * OTPModal — second-factor confirmation for SuperAdmin destructive actions.
 *
 * Lifecycle:
 *   1. Caller mounts <OTPModal action="..." ... onVerified={({challengeId, code}) => ...} />
 *   2. On mount, POST /api/superadmin/otp/request → shows email-sent state
 *      with a 6-digit input + attempts counter + resend affordance.
 *   3. On submit, we DO NOT re-verify server-side — we hand the caller
 *      {challengeId, code} and they inject it as X-OTP-Challenge-Id +
 *      X-OTP-Code headers on their real destructive request. The server
 *      consumes the challenge inside the action handler (atomic).
 *   4. If the caller's action fails with 403 otp_wrong / otp_expired we
 *      re-render the input with the server's remaining-attempts hint.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Mail, RefreshCw, X } from 'lucide-react';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';

const ACTION_LABEL = {
  bulk_delete:       'Bulk delete all components',
  master_wipe:       'Master wipe — complete inventory reset',
  delete_one:        'Delete component',
  edit_component:    'Edit component details',
  quantity_update:   'Adjust component stock',
};

export default function OTPModal({
  open, action, context, confirmLabel, danger, onCancel, onVerified,
}) {
  const [challengeId, setChallengeId] = useState(null);
  const [emailHint, setEmailHint] = useState(null);
  const [emailSent, setEmailSent] = useState(null);
  const [code, setCode] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);

  const requestOtp = async () => {
    setRequesting(true);
    setError(null);
    setCode('');
    setRemaining(null);
    try {
      const { data } = await apiClient.post(endpoints.otp.request, { action, context });
      setChallengeId(data.challenge_id);
      setEmailHint(data.email_hint);
      setEmailSent(!!data.email_sent);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to request OTP.');
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => {
    if (open) requestOtp();
    else {
      setChallengeId(null); setEmailHint(null); setEmailSent(null);
      setCode(''); setError(null); setRemaining(null);
    }
  }, [open, action]);

  if (!open) return null;

  const submit = async () => {
    if (!challengeId || !/^\d{6}$/.test(code) || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      await Promise.resolve(onVerified({ challengeId, code }));
    } catch (e) {
      const payload = e?.response?.data?.detail;
      if (typeof payload === 'object' && payload) {
        setError(payload.message || payload.error || 'Verification failed.');
        if (typeof payload.attempts_remaining === 'number') setRemaining(payload.attempts_remaining);
        if (payload.error === 'otp_revoked' || payload.revoked) setChallengeId(null);
      } else {
        setError(typeof payload === 'string' ? payload : 'Action failed.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const label = ACTION_LABEL[action] || action;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
      data-testid="otp-modal"
      role="dialog"
      aria-modal="true"
    >
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={20} style={{ color: danger ? '#B04A3C' : '#0F766E' }} />
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6b7280' }}>Security code</div>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginTop: 2 }}>{label}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
            data-testid="otp-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '18px 22px' }}>
          {context && (
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              <strong style={{ color: '#111827' }}>Scope:</strong> {context}
            </div>
          )}

          {requesting ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              <RefreshCw size={16} className="spin" style={{ marginRight: 6, verticalAlign: -3 }} />
              Issuing OTP…
            </div>
          ) : !challengeId ? (
            <div style={{ padding: '16px 0', color: '#B04A3C', fontSize: 14 }}>
              {error || 'Could not issue a code. Try again.'}
              <div style={{ marginTop: 12 }}>
                <button type="button" onClick={requestOtp} className="ap-btn ap-btn-ghost" data-testid="otp-retry-btn">
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px',
                  background: emailSent ? '#f0f9ff' : '#fef3f2',
                  border: `1px solid ${emailSent ? '#bae6fd' : '#fecaca'}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: emailSent ? '#075985' : '#7f1d1d',
                  marginBottom: 14,
                }}
                data-testid="otp-email-status"
              >
                <Mail size={16} />
                {emailSent ? (
                  <span>Code sent to <strong>{emailHint || 'your registered email'}</strong>. Valid for 10 minutes.</span>
                ) : (
                  <span>
                    Email delivery is not configured yet — code is still logged server-side.
                    Ask your admin to add <code style={{ background: 'white', padding: '0 4px', borderRadius: 3 }}>RESEND_API_KEY</code> and resend.
                  </span>
                )}
              </div>

              <label style={{ display: 'block', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }} htmlFor="otp-code-input">
                Enter 6-digit code
              </label>
              <input
                id="otp-code-input"
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter' && /^\d{6}$/.test(code)) submit(); }}
                data-testid="otp-code-input"
                style={{
                  width: '100%', padding: '12px 14px', fontSize: 22, letterSpacing: 10,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 8,
                  color: '#111827',
                }}
                placeholder="••••••"
              />

              {error && (
                <div data-testid="otp-error" style={{ marginTop: 10, color: '#B04A3C', fontSize: 13 }}>
                  {error}{remaining !== null ? ` · ${remaining} attempt${remaining === 1 ? '' : 's'} left` : ''}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={requesting}
                  className="ap-btn ap-btn-ghost ap-btn-sm"
                  data-testid="otp-resend-btn"
                >
                  <RefreshCw size={13} /> Resend code
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={onCancel} className="ap-btn ap-btn-ghost" data-testid="otp-cancel-btn">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={verifying || !/^\d{6}$/.test(code)}
                    className={`ap-btn ${danger ? 'ap-btn-danger' : 'ap-btn-primary'}`}
                    data-testid="otp-submit-btn"
                  >
                    {verifying ? 'Verifying…' : (confirmLabel || 'Confirm')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite;}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
